import { useContext, useEffect, useRef } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useApplePayTabHandoff } from '@/hooks/useApplePayTabHandoff';
import { useApplePayTabFallbackReason } from '@/hooks/useApplePayTabFallbackGate';
import { mixpanelService } from '@/services/mixpanelService';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import type { ExpressCheckoutComponentProps } from '@/types/expressCheckout';
import { FakeApplePayButton } from './FakeApplePayButton';

/**
 * iOS <= 16 cross-origin-iframe Apple Pay fallback (drop-in for any Apple
 * Pay render slot — APM form or express row). Renders the fake button;
 * a click opens the checkout in a top-level tab where the REAL Apple Pay
 * button works, while this tab shows the processing loader until the
 * player closes the tab.
 *
 * Reports `onReady(true)` immediately: the button is always usable, so
 * templates must not swap away (`useExpressWalletFallback`) and the
 * express row must not collapse the slot.
 */
export const ApplePayTabFallback = ({ onReady }: ExpressCheckoutComponentProps) => {
  const context = useContext(CheckoutContext);
  const { openApplePayTab } = useApplePayTabHandoff();
  const reason = useApplePayTabFallbackReason();
  const hasReportedRef = useRef(false);
  // Late-bound so the one-shot report below can read the reason without
  // re-running when a domain failure latches after mount.
  const reasonRef = useRef(reason);
  reasonRef.current = reason;

  useEffect(() => {
    if (hasReportedRef.current) return;
    hasReportedRef.current = true;
    onReady?.(true);
    mixpanelService.track(MixpanelEvents.APPLE_PAY_TAB_FALLBACK_RENDERED, {
      ...(getMixpanelProperties(context) as Record<string, unknown>),
      fallback_reason: reasonRef.current,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot mount report
  }, []);

  return <FakeApplePayButton onClick={openApplePayTab} />;
};
